// React
import React, { useMemo, useState } from "react";

// Material UI
import {
    Box,
    Grid,
    Typography,
    TextField,
    IconButton,
    Chip,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";

export default function FeedbackSection({
    title = "Regenerate",
    helperText = "Provide your prompt with files to regenerate the above markdown content.",
    value,
    onChange,
    rows = 4,
    multiple = true,
    onFilesChange, // optional: (files: File[]) => void
    style,
}) {
    const [attachments, setAttachments] = useState([]);

    // unique id for the hidden input
    const inputId = useMemo(
        () => `feedback-attachment-${Math.random().toString(36).slice(2)}`,
        []
    );

    const fileKey = (f) => `${f.name}-${f.size}-${f.lastModified}`;

    const dedupeFiles = (filesArr) => {
        const map = new Map();
        filesArr.forEach((f) => map.set(fileKey(f), f));
        return Array.from(map.values());
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        const next = dedupeFiles([...attachments, ...files]);
        setAttachments(next);
        if (onFilesChange) onFilesChange(next);
        // reset the input so selecting the same file again re-triggers onChange
        e.target.value = "";
    };

    const handleRemove = (key) => {
        const next = attachments.filter((f) => fileKey(f) !== key);
        setAttachments(next);
        if (onFilesChange) onFilesChange(next);
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    };

    return (
        <Grid container style={style}>
            <Grid item xs={12}>
                <Typography variant="body1" style={{ marginBottom: 10 }}>
                    {helperText}
                </Typography>
            </Grid>

            <Grid item xs={12} style={{ marginTop: 10, marginBottom: 10 }}>
                <Box position="relative">
                    <TextField
                        multiline
                        fullWidth
                        rows={rows}
                        placeholder="Enter your prompt here..."
                        style={{ backgroundColor: "white" }}
                        variant="outlined"
                        value={value}
                        onChange={(e) => onChange?.(e.target.value)}
                        // make room so the icon doesn't overlap text
                        inputProps={{ style: { paddingBottom: 36 } }}
                    />

                    {/* Hidden file input */}
                    <input
                        id={inputId}
                        type="file"
                        multiple={multiple}
                        accept=".pdf,application/pdf"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />

                    {/* Attach icon anchored bottom-left inside the field */}
                    <label htmlFor={inputId}>
                        <IconButton
                            component="span"
                            size="small"
                            style={{ position: "absolute", left: 8, bottom: 8 }}
                            aria-label="Attach file"
                        >
                            <AttachFileIcon fontSize="medium" />
                        </IconButton>
                    </label>
                </Box>

                {/* Attached files as chips */}
                {attachments.length > 0 && (
                    <Box mt={1} display="flex" flexWrap="wrap">
                        {attachments.map((file) => {
                            const key = fileKey(file);
                            return (
                                <Box key={key} mr={1} mb={1}>
                                    <Chip
                                        label={`${file.name} • ${formatBytes(file.size)}`}
                                        onDelete={() => handleRemove(key)}
                                        deleteIcon={<CloseIcon fontSize="small" />}
                                        variant="default"
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Grid>
        </Grid>
    );
}
